/* eslint-env browser */

(function () {
    const MAIN_APP_URL_PARTS = ['https://', 'www.guidestination.com/'];
    const EXPLORE_ACTIVITIES_LABEL = 'explore activities';
    const PORTAL_RETURN_TARGETS = {
        partner: {
            href: 'https://partner.guidestination.com/',
            label: 'Back to Partner Portal',
            mobileLabel: 'Partner Portal'
        },
        provider: {
            href: 'https://provider.guidestination.com/',
            label: 'Back to Provider Portal',
            mobileLabel: 'Provider Portal'
        }
    };
    const PORTAL_QUERY_PARAMS = ['portal', 'portal_view', 'portalView', 'portal_referrer', 'portalReferrer'];
    const PRESERVED_BLOG_CONTEXT_QUERY_PARAMS = ['referrer', ...PORTAL_QUERY_PARAMS];
    const LINK_MAP = [
        {match: /^#\/portal\/signin\/?$/i, href: '/members/signin/'},
        {match: /^#\/portal\/signup\/?$/i, href: '/members/signup/'},
        {match: /^#\/portal\/?$/i, href: '/members/signup/'},
        {match: /^#\/portal\/account\/?$/i, href: '/members/signin/'}
    ];

    function getMainAppUrl() {
        return MAIN_APP_URL_PARTS.join('');
    }

    function openMainAppLandingPage() {
        window.open(getMainAppUrl(), '_blank', 'noopener,noreferrer');
    }

    function getReplacement(href, portalAction) {
        if (portalAction === 'signin') {
            return '/members/signin/';
        }

        if (portalAction === 'signup') {
            return '/members/signup/';
        }

        if (portalAction === 'account') {
            return '/members/signin/';
        }

        for (const entry of LINK_MAP) {
            if (entry.match.test(href || '')) {
                return entry.href;
            }
        }

        return null;
    }

    function rewritePortalLinks(root) {
        const links = root.querySelectorAll('a[href], a[data-portal]');

        links.forEach((link) => {
            const replacement = getReplacement(link.getAttribute('href'), link.dataset.portal);

            if (!replacement) {
                return;
            }

            link.setAttribute('href', replacement);
            link.removeAttribute('data-portal');
        });
    }

    function isExternalNavigationLink(link) {
        const href = link.getAttribute('href') || '';

        if (!href || href.startsWith('#')) {
            return false;
        }

        let url;

        try {
            url = new URL(href, window.location.href);
        } catch (error) {
            return false;
        }

        if (!['http:', 'https:'].includes(url.protocol)) {
            return false;
        }

        return url.origin !== window.location.origin;
    }

    function markExternalNavigationLinks(root) {
        const links = root.querySelectorAll([
            '.gd-nav a',
            '.gd-head-actions a',
            '.gd-footer-nav a',
            '.gd-footer-actions a',
            '.gd-hero-actions a',
            '.gd-inline-cta-actions a'
        ].join(', '));

        links.forEach((link) => {
            if (link.hasAttribute('data-gd-portal-return-link')) {
                return;
            }

            if (!isExternalNavigationLink(link)) {
                return;
            }

            link.setAttribute('target', '_blank');
            link.setAttribute('rel', 'noopener noreferrer');
        });
    }

    function normalizePortalReferrer(value) {
        const normalized = String(value || '').trim().toLowerCase().replace(/[\s_]+/g, '-');

        if (normalized === 'partner' || normalized === 'partner-portal') {
            return 'partner';
        }

        if (normalized === 'provider' || normalized === 'provider-portal') {
            return 'provider';
        }

        return null;
    }

    function getPortalReferrer() {
        try {
            const searchParams = new URL(window.location.href).searchParams;
            const referrers = [];

            PORTAL_QUERY_PARAMS.forEach((param) => {
                referrers.push(...searchParams.getAll(param));
            });

            referrers.push(...searchParams.getAll('referrer'));

            for (const referrer of referrers) {
                const normalized = normalizePortalReferrer(referrer);

                if (normalized) {
                    return normalized;
                }
            }
        } catch (error) {
            return null;
        }

        return null;
    }

    function applyPreservedBlogContextToUrl(value) {
        if (!value) {
            return value;
        }

        try {
            const currentUrl = new URL(window.location.href);
            const targetUrl = new URL(value, window.location.href);

            PRESERVED_BLOG_CONTEXT_QUERY_PARAMS.forEach((param) => {
                const values = currentUrl.searchParams.getAll(param);

                if (!values.length) {
                    return;
                }

                targetUrl.searchParams.delete(param);
                values.forEach((paramValue) => {
                    targetUrl.searchParams.append(param, paramValue);
                });
            });

            return targetUrl.toString();
        } catch (error) {
            return value;
        }
    }

    function initPortalReturnLinks(root) {
        const links = root.querySelectorAll('[data-gd-portal-return-link]');

        if (!links.length) {
            return;
        }

        const portalReferrer = getPortalReferrer();
        const target = portalReferrer ? PORTAL_RETURN_TARGETS[portalReferrer] : null;

        links.forEach((link) => {
            if (!target) {
                link.hidden = true;
                link.removeAttribute('href');
                link.textContent = '';
                return;
            }

            link.href = target.href;
            const label = link.classList.contains('gd-portal-return-link--mobile') ? target.mobileLabel : target.label;
            link.textContent = label;
            link.setAttribute('aria-label', target.label);
            link.hidden = false;
        });

        if (portalReferrer) {
            document.body.classList.add('gd-has-portal-return', 'gd-has-portal-return--' + portalReferrer);
        }
    }

    function normalizeLinkText(value) {
        return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
    }

    function rewriteExploreActivitiesLinks(root) {
        const controls = root.querySelectorAll([
            '.gd-head-actions [data-gd-main-app-cta]',
            '.gd-head-actions .gd-button--primary',
            '.gd-mobile-explore-cta'
        ].join(', '));

        controls.forEach((control) => {
            const isMainAppCta = control.hasAttribute('data-gd-main-app-cta');
            const isMobileExploreCta = control.classList.contains('gd-mobile-explore-cta');

            if (!isMainAppCta && !isMobileExploreCta && normalizeLinkText(control.textContent) !== EXPLORE_ACTIVITIES_LABEL) {
                return;
            }

            control.setAttribute('type', 'button');
            control.setAttribute('data-gd-main-app-cta', '');
            control.removeAttribute('href');
            control.removeAttribute('target');
            control.removeAttribute('rel');
            control.onclick = function () {
                openMainAppLandingPage();
                return false;
            };
        });
    }

    function getInternalPathSegments(link) {
        const href = link.getAttribute('href') || '';

        if (!href || href.startsWith('#')) {
            return null;
        }

        try {
            const url = new URL(href, window.location.origin);

            if (url.origin !== window.location.origin) {
                return null;
            }

            return url.pathname.replace(/\/+$/, '').split('/').filter(Boolean);
        } catch (error) {
            return null;
        }
    }

    function shouldRemovePrimaryNavLink(link) {
        const text = normalizeLinkText(link.textContent);

        if (text === 'home' || text === 'about') {
            return true;
        }

        const segments = getInternalPathSegments(link);

        if (!segments) {
            return false;
        }

        if (segments.length === 0) {
            return true;
        }

        if (segments.length === 1 && ['en', 'th', 'fr'].includes(segments[0])) {
            return true;
        }

        const lastSegment = segments[segments.length - 1];
        const isLocalizedAbout = segments.length === 2 && ['en', 'th', 'fr'].includes(segments[0]);

        return ['about', 'about-us'].includes(lastSegment) && (segments.length === 1 || isLocalizedAbout);
    }

    function prunePrimaryNavigation(root) {
        const nav = root.querySelector('.gd-nav .nav');

        if (!nav) {
            return;
        }

        nav.querySelectorAll('a[href]').forEach((link) => {
            if (!shouldRemovePrimaryNavLink(link)) {
                return;
            }

            const item = link.closest('li');

            if (item) {
                item.remove();
                return;
            }

            link.remove();
        });
    }

    function closeMenuOnNavigation() {
        document.addEventListener('click', function (event) {
            const link = event.target.closest('a[href]');

            if (!link || !link.closest('.gd-nav, .gd-head-actions, .gd-mobile-top-tools, .gd-footer-nav')) {
                return;
            }

            closeTopNavDropdowns();
        });
    }

    function closeLanguageMenus() {
        document.querySelectorAll('[data-gd-language-menu]').forEach((menu) => {
            menu.open = false;
        });
    }

    function closeTopNavDropdowns() {
        closeLanguageMenus();
        document.querySelectorAll('[data-gd-nav-search]').forEach(closeSearch);
        document.body.classList.remove('gh-head-open');
    }

    function normalizeLocale(value) {
        const normalized = String(value || '').trim().toLowerCase();

        if (['en', 'th', 'fr'].includes(normalized)) {
            return normalized;
        }

        return null;
    }

    function persistLocale(locale) {
        if (!locale) {
            return;
        }

        const storageKeys = ['gd_preferred_language', 'preferredLanguage', 'language'];

        try {
            storageKeys.forEach(key => window.localStorage.setItem(key, locale));
        } catch (error) {
            // Ignore storage access failures such as private browsing restrictions.
        }

        try {
            storageKeys.forEach(key => window.sessionStorage.setItem(key, locale));
        } catch (error) {
            // Ignore storage access failures such as private browsing restrictions.
        }

        document.cookie = 'gd_preferred_language=' + encodeURIComponent(locale) + '; Path=/; Max-Age=31536000; SameSite=Lax';
    }

    function readStoredLocale() {
        const storageKeys = ['gd_preferred_language', 'preferredLanguage', 'language'];

        for (const key of storageKeys) {
            try {
                const stored = normalizeLocale(window.localStorage.getItem(key));
                if (stored) {
                    return stored;
                }
            } catch (error) {
                break;
            }
        }

        for (const key of storageKeys) {
            try {
                const stored = normalizeLocale(window.sessionStorage.getItem(key));
                if (stored) {
                    return stored;
                }
            } catch (error) {
                break;
            }
        }

        const cookies = document.cookie.split(';').map(entry => entry.trim()).filter(Boolean);
        for (const entry of cookies) {
            const [name, ...rest] = entry.split('=');
            if (name === 'gd_preferred_language') {
                return normalizeLocale(decodeURIComponent(rest.join('=')));
            }
        }

        return null;
    }

    function isCrawler() {
        const userAgent = navigator.userAgent || '';

        return /(bot|crawler|spider|crawling|bingpreview|facebookexternalhit|slurp|duckduckbot|baiduspider)/i.test(userAgent);
    }

    function resolveCollectionRedirect(locale, pathname) {
        const normalizedLocale = normalizeLocale(locale);
        if (!normalizedLocale) {
            return null;
        }

        const cleanedPath = pathname.replace(/\/+$/, '') || '/';
        const pageMatch = cleanedPath.match(/^\/(?:(th|fr)\/)?page\/(\d+)$/);

        if (pageMatch) {
            const prefix = normalizedLocale === 'en' ? '' : '/' + normalizedLocale;
            return prefix + '/page/' + pageMatch[2] + '/';
        }

        if (/^\/(?:(th|fr))?$/.test(cleanedPath)) {
            return normalizedLocale === 'en' ? '/' : '/' + normalizedLocale + '/';
        }

        return null;
    }

    function initLocaleSwitcher() {
        const switchers = document.querySelectorAll('[data-gd-language-switcher]');

        switchers.forEach((switcher) => {
            const select = switcher.querySelector('[data-gd-language-select]');

            if (select) {
                select.addEventListener('change', function () {
                    const option = select.options[select.selectedIndex];

                    if (!option) {
                        return;
                    }

                    const locale = normalizeLocale(option.getAttribute('data-gd-locale-option'));
                    const target = applyPreservedBlogContextToUrl(option.value);

                    persistLocale(locale);

                    if (target) {
                        closeTopNavDropdowns();
                        window.location.href = target;
                    }
                });
            }
        });

        document.querySelectorAll('[data-gd-locale-link]').forEach((link) => {
            const target = applyPreservedBlogContextToUrl(link.getAttribute('href'));

            if (target) {
                link.setAttribute('href', target);
            }

            link.addEventListener('click', function () {
                persistLocale(link.getAttribute('data-gd-locale-link'));
                closeTopNavDropdowns();
            });
        });

        if (isCrawler()) {
            return;
        }

        const currentLocale = normalizeLocale(document.documentElement.getAttribute('data-gd-current-locale')) || 'en';
        const storedLocale = readStoredLocale();
        const currentCollectionUrl = resolveCollectionRedirect(currentLocale, window.location.pathname);

        if (!currentCollectionUrl || !storedLocale || storedLocale === currentLocale) {
            return;
        }

        const target = resolveCollectionRedirect(storedLocale, window.location.pathname);

        if (!target || target === window.location.pathname) {
            return;
        }

        window.location.replace(target + window.location.search + window.location.hash);
    }

    function initLanguageMenus() {
        const languageMenus = document.querySelectorAll('[data-gd-language-menu]');

        languageMenus.forEach((menu) => {
            menu.addEventListener('toggle', function () {
                if (!menu.open) {
                    return;
                }

                languageMenus.forEach((otherMenu) => {
                    if (otherMenu !== menu) {
                        otherMenu.open = false;
                    }
                });

                document.querySelectorAll('[data-gd-nav-search]').forEach(closeSearch);
            });
        });

        document.addEventListener('click', function (event) {
            if (event.target.closest('[data-gd-language-menu]')) {
                return;
            }

            languageMenus.forEach((menu) => {
                menu.open = false;
            });
        });
    }

    let searchPostsPromise = null;

    function addUniqueSearchApiBaseUrl(urls, value) {
        try {
            const url = new URL(value, window.location.href);
            url.hash = '';
            url.search = '';

            const normalized = url.href.replace(/\/?$/, '/');

            if (!urls.includes(normalized)) {
                urls.push(normalized);
            }
        } catch (error) {
            // Ignore invalid search API candidates.
        }
    }

    function addDirectBlogApiCandidate(urls, sodoSearchUrl) {
        const pathSegments = sodoSearchUrl.pathname.split('/').filter(Boolean);

        if (!pathSegments.includes('blog')) {
            return;
        }

        const hostname = sodoSearchUrl.hostname.replace(/^www\./i, '');

        if (!hostname || sodoSearchUrl.hostname.toLowerCase().startsWith('blog.')) {
            return;
        }

        addUniqueSearchApiBaseUrl(
            urls,
            sodoSearchUrl.protocol + '//blog.' + hostname + '/ghost/api/content/'
        );
    }

    function resolveSearchApiBaseUrls(scriptTag) {
        const urls = [];

        if (scriptTag.dataset.api) {
            addUniqueSearchApiBaseUrl(urls, scriptTag.dataset.api);
        }

        try {
            const sodoSearchUrl = new URL(scriptTag.dataset.sodoSearch || window.location.origin, window.location.href);

            addDirectBlogApiCandidate(urls, sodoSearchUrl);
            addUniqueSearchApiBaseUrl(urls, new URL('ghost/api/content/', sodoSearchUrl).href);
            addUniqueSearchApiBaseUrl(urls, new URL('/ghost/api/content/', sodoSearchUrl.origin).href);
        } catch (error) {
            addUniqueSearchApiBaseUrl(urls, '/ghost/api/content/');
        }

        return urls;
    }

    function getSearchConfig() {
        const scriptTag = document.querySelector('script[data-sodo-search]');

        if (!scriptTag || !scriptTag.dataset.key) {
            return null;
        }

        return {
            apiKey: scriptTag.dataset.key,
            apiBaseUrls: resolveSearchApiBaseUrls(scriptTag)
        };
    }

    function stripTags(value) {
        return String(value || '').replace(/<[^>]*>/g, ' ');
    }

    function decodeEntities(value) {
        const element = document.createElement('textarea');
        element.innerHTML = String(value || '');
        return element.value;
    }

    function normalizeSearchText(value) {
        return decodeEntities(stripTags(value))
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/\s+/g, ' ')
            .trim()
            .toLowerCase();
    }

    function escapeHtml(value) {
        return String(value || '')
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#039;');
    }

    function getCurrentLocale() {
        return normalizeLocale(document.documentElement.getAttribute('data-gd-current-locale')) || 'en';
    }

    function getLocaleFromPathname(pathname) {
        const segments = String(pathname || '').split('/').filter(Boolean);

        if (normalizeLocale(segments[0])) {
            return normalizeLocale(segments[0]);
        }

        if (segments[0] === 'blog' && normalizeLocale(segments[1])) {
            return normalizeLocale(segments[1]);
        }

        return 'en';
    }

    function postMatchesCurrentLocale(post) {
        const currentLocale = getCurrentLocale();

        try {
            const url = new URL(post.url || '', window.location.origin);
            return getLocaleFromPathname(url.pathname) === currentLocale;
        } catch (error) {
            return true;
        }
    }

    function getBlogProxyPrefix() {
        const segments = window.location.pathname.split('/').filter(Boolean);

        if (segments[0] === 'blog') {
            return '/blog';
        }

        if (['en', 'th', 'fr'].includes(segments[0]) && segments[1] === 'blog') {
            return '/' + segments[0] + '/blog';
        }

        return '';
    }

    function getSearchResultHref(href) {
        try {
            const url = new URL(href || '#', window.location.href);
            const proxyPrefix = getBlogProxyPrefix();
            const targetPath = url.pathname === '/' ? '/' : url.pathname.replace(/\/+$/, '') + '/';

            if (proxyPrefix && !targetPath.startsWith(proxyPrefix + '/')) {
                return proxyPrefix + (targetPath === '/' ? '/' : targetPath) + url.search + url.hash;
            }

            if (url.origin === window.location.origin) {
                return targetPath + url.search + url.hash;
            }

            return url.href;
        } catch (error) {
            return '#';
        }
    }

    function getSearchablePosts() {
        if (searchPostsPromise) {
            return searchPostsPromise;
        }

        const config = getSearchConfig();

        if (!config || !config.apiBaseUrls.length) {
            searchPostsPromise = Promise.reject({message: 'Search configuration is missing'});
            return searchPostsPromise;
        }

        searchPostsPromise = (async function () {
            let lastError = null;

            for (const apiBaseUrl of config.apiBaseUrls) {
                const requestUrl = new URL('search-index/posts/', apiBaseUrl);
                requestUrl.searchParams.set('key', config.apiKey);

                try {
                    const response = await fetch(requestUrl.toString());

                    if (!response.ok) {
                        lastError = {message: 'Search index request failed with status ' + response.status};
                        continue;
                    }

                    const payload = await response.json();
                    return Array.isArray(payload.posts) ? payload.posts : [];
                } catch (error) {
                    lastError = error;
                }
            }

            return Promise.reject(lastError || {message: 'Search index request failed'});
        })()
            .catch((error) => {
                // eslint-disable-next-line no-console
                console.error('Failed to load navigation search index:', error);
                searchPostsPromise = null;
                throw error;
            });

        return searchPostsPromise;
    }

    function scorePostForQuery(post, normalizedQuery) {
        const title = normalizeSearchText(post.title);
        const excerpt = normalizeSearchText(post.excerpt);
        const slug = normalizeSearchText(post.slug);

        if (!title && !excerpt && !slug) {
            return 0;
        }

        if (title === normalizedQuery) {
            return 100;
        }

        if (title.startsWith(normalizedQuery)) {
            return 80;
        }

        if (title.includes(normalizedQuery)) {
            return 60;
        }

        if (slug.includes(normalizedQuery)) {
            return 42;
        }

        if (excerpt.includes(normalizedQuery)) {
            return 32;
        }

        return 0;
    }

    function searchPosts(posts, query) {
        const normalizedQuery = normalizeSearchText(query);

        if (normalizedQuery.length < 2) {
            return [];
        }

        return posts
            .filter(postMatchesCurrentLocale)
            .map(post => ({
                post,
                score: scorePostForQuery(post, normalizedQuery)
            }))
            .filter(entry => entry.score > 0)
            .sort((a, b) => b.score - a.score)
            .slice(0, 6)
            .map(entry => entry.post);
    }

    function renderSearchMessage(resultsElement, message) {
        resultsElement.innerHTML = '<div class="gd-nav-search__message">' + escapeHtml(message) + '</div>';
    }

    function renderSearchResults(resultsElement, posts, emptyMessage) {
        if (!posts.length) {
            renderSearchMessage(resultsElement, emptyMessage);
            return;
        }

        resultsElement.innerHTML = posts.map((post) => {
            const title = decodeEntities(stripTags(post.title || 'Untitled'));
            const excerpt = decodeEntities(stripTags(post.excerpt || ''));
            const href = getSearchResultHref(post.url);

            return [
                '<a class="gd-nav-search__result" role="option" href="',
                escapeHtml(href),
                '">',
                '<span class="gd-nav-search__result-title">',
                escapeHtml(title),
                '</span>',
                excerpt ? '<span class="gd-nav-search__result-excerpt">' + escapeHtml(excerpt) + '</span>' : '',
                '</a>'
            ].join('');
        }).join('');
    }

    function closeSearch(searchRoot) {
        const panel = searchRoot.querySelector('[data-gd-nav-search-panel]');
        const toggle = searchRoot.querySelector('[data-gd-nav-search-toggle]');

        if (panel) {
            panel.hidden = true;
        }

        if (toggle) {
            toggle.setAttribute('aria-expanded', 'false');
        }
    }

    function openSearch(searchRoot) {
        const panel = searchRoot.querySelector('[data-gd-nav-search-panel]');
        const toggle = searchRoot.querySelector('[data-gd-nav-search-toggle]');
        const input = searchRoot.querySelector('[data-gd-nav-search-input]');
        const resultsElement = searchRoot.querySelector('[data-gd-nav-search-results]');

        document.querySelectorAll('[data-gd-nav-search]').forEach((otherSearchRoot) => {
            if (otherSearchRoot !== searchRoot) {
                closeSearch(otherSearchRoot);
            }
        });

        if (panel) {
            panel.hidden = false;
        }

        if (toggle) {
            toggle.setAttribute('aria-expanded', 'true');
        }

        if (resultsElement && panel) {
            renderSearchMessage(resultsElement, panel.dataset.searchReady || 'Type at least 2 characters');
        }

        if (input) {
            window.setTimeout(() => input.focus(), 0);
        }
    }

    function initNavigationSearch() {
        const searchRoots = document.querySelectorAll('[data-gd-nav-search]');

        searchRoots.forEach((searchRoot) => {
            const toggle = searchRoot.querySelector('[data-gd-nav-search-toggle]');
            const panel = searchRoot.querySelector('[data-gd-nav-search-panel]');
            const input = searchRoot.querySelector('[data-gd-nav-search-input]');
            const resultsElement = searchRoot.querySelector('[data-gd-nav-search-results]');

            if (!toggle || !panel || !input || !resultsElement) {
                return;
            }

            toggle.addEventListener('click', function () {
                if (panel.hidden) {
                    closeLanguageMenus();
                    openSearch(searchRoot);
                    return;
                }

                closeSearch(searchRoot);
            });

            input.addEventListener('input', function () {
                const query = input.value;

                if (normalizeSearchText(query).length < 2) {
                    renderSearchMessage(resultsElement, panel.dataset.searchReady || 'Type at least 2 characters');
                    return;
                }

                renderSearchMessage(resultsElement, panel.dataset.searchLoading || 'Searching...');

                getSearchablePosts().then((posts) => {
                    if (input.value !== query) {
                        return;
                    }

                    renderSearchResults(resultsElement, searchPosts(posts, query), panel.dataset.searchEmpty || 'No matching posts found');
                }).catch(() => {
                    if (input.value !== query) {
                        return;
                    }

                    renderSearchMessage(resultsElement, panel.dataset.searchError || 'Search is temporarily unavailable');
                });
            });

            panel.addEventListener('submit', function (event) {
                event.preventDefault();

                const firstResult = resultsElement.querySelector('.gd-nav-search__result');
                if (firstResult) {
                    window.location.href = firstResult.getAttribute('href');
                }
            });
        });

        document.addEventListener('click', function (event) {
            if (event.target.closest('[data-gd-nav-search]')) {
                return;
            }

            searchRoots.forEach(closeSearch);
        });

        document.addEventListener('keydown', function (event) {
            if (event.key !== 'Escape') {
                return;
            }

            searchRoots.forEach(closeSearch);
        });
    }

    document.addEventListener('DOMContentLoaded', function () {
        rewritePortalLinks(document);
        rewriteExploreActivitiesLinks(document);
        prunePrimaryNavigation(document);
        initPortalReturnLinks(document);
        markExternalNavigationLinks(document);
        closeMenuOnNavigation();
        initLocaleSwitcher();
        initLanguageMenus();
        initNavigationSearch();
    });
})();
